import { Button } from "@shared/ui";
import { Info, Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";
import styles from "./DataUpdateBanner.module.scss";

export type DataStatus = "Pending" | "Success" | "Failed";

interface DataUpdateBannerProps {
  title: string;
  status: DataStatus;
  lastUpdatedAt?: string;
  onRefresh: () => Promise<void>;
  isLoading?: boolean;
}

export function DataUpdateBanner({
  title,
  status,
  lastUpdatedAt,
  onRefresh,
  isLoading = false,
}: DataUpdateBannerProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusMessage = () => {
    if (status === "Success" && lastUpdatedAt) {
      return (
        <>
          <span className={styles.lastUpdatedText}>
            Last updated: {formatDate(lastUpdatedAt)}
          </span>
        </>
      );
    }
    if (status === "Pending") {
      return "Data is being updated. Please check back shortly.";
    }
    if (status === "Failed") {
      return "Failed to update data. Please try refreshing.";
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} at ${timeStr}`;
  };

  const getStatusColor = () => {
    switch (status) {
      case "Success":
        return styles.statusSuccess;
      case "Pending":
        return styles.statusPending;
      case "Failed":
        return styles.statusFailed;
      default:
        return "";
    }
  };

  const statusMessage = getStatusMessage();

  return (
    <div className={`${styles.banner} ${getStatusColor()}`}>
      <Info size={14} className={styles.infoIcon} />
      <div className={styles.bannerContent}>
        <div className={styles.bannerText}>
          {title}
          {statusMessage && (
            <>
              <br />
              {statusMessage}
            </>
          )}
        </div>
      </div>
      <Button
        type="text"
        size="small"
        icon={
          isRefreshing || isLoading ? (
            <Loader2 size={14} className={styles.refreshIcon} />
          ) : (
            <RefreshCw size={14} className={styles.refreshIcon} />
          )
        }
        onClick={handleRefresh}
        disabled={isRefreshing || isLoading}
        className={styles.refreshButton}
      />
    </div>
  );
}
